/**
 * **Service hours** (ADR 0029, accepted Oct 13; `ServiceHours.dc.html`). A volunteer's hours for a
 * service-hours letter, from the booth's check-ins and check-outs only — nothing typed. One line
 * per check-in that still stands: counted when a check-out closes it (the booth's, or a chair's
 * "closed at shift end"), open when nothing has yet, and an open line is left off the letter until
 * it is a state. A correction replaces the row it names; an undone check-in is not there at all.
 *
 * READ BY: Letters to sign · the letter · a group leader's list · On the person · the family
 * page's Request a letter · Check a code (the hours as signed). The One Engine page does not name
 * this one yet — it is new with ADR 0029.
 */

export interface ServiceCheckIn {
  id: string;
  shiftId: string;
  personId: string;
  at: Date;
  corrects: string | null;
  byKind: string | null;
}

export interface ServiceCheckOut {
  id: string;
  shiftId: string;
  personId: string;
  checkInId: string | null;
  at: Date;
  workedMinutes: number | null;
  corrects: string | null;
  byKind: string | null;
  byPersonId: string | null;
}

export interface ServiceShift {
  id: string;
  title: string;
  areaName: string;
  startsAt: Date;
  endsAt: Date;
}

export interface ServiceLine {
  shiftId: string;
  title: string;
  areaName: string;
  startsAt: Date;
  endsAt: Date;
  inAt: Date;
  outAt: Date | null;
  /** Null while the line is open. */
  minutes: number | null;
  /** counted — a check-out closes it; open — nothing has yet. */
  state: 'counted' | 'open';
  /** Whose hand closed it: the booth, a chair (or the volunteer team), or the 1.0 import. */
  closedBy: 'booth' | 'chair' | 'import' | null;
  closedByPersonId: string | null;
}

export interface ServiceHours {
  personId: string;
  lines: ServiceLine[];
  /** The counted lines only — what a letter may carry. */
  countedMinutes: number;
  openLines: number;
}

const CHAIR_KINDS = new Set(['chair', 'volunteer_chairs']);

/**
 * The check-ins that still stand for one person, each with the check-out that closes it (or
 * none yet). A correction replaces the row it names; an undone check-in is not there at all. The
 * one rule for which rows count — the hours here and a late match's credit (ADR 0060) read it.
 */
export function standingPairs<I extends ServiceCheckIn, O extends ServiceCheckOut>(
  personId: string,
  checkIns: readonly I[],
  checkOuts: readonly O[],
): { checkIn: I; checkOut: O | null }[] {
  const ins = checkIns.filter((r) => r.personId === personId);
  const undone = new Set(
    ins.filter((r) => r.byKind === 'undone' && r.corrects).map((r) => r.corrects),
  );
  const standing = ins.filter(
    (r) => r.byKind !== 'undone' && r.corrects === null && !undone.has(r.id),
  );
  const outs = checkOuts.filter((r) => r.personId === personId);
  // A correction replaces the row it names; the newest word on a check-out is the one that counts.
  const replaced = new Set(outs.filter((o) => o.corrects).map((o) => o.corrects));
  const current = outs.filter((o) => !replaced.has(o.id));
  return standing.map((i) => ({
    checkIn: i,
    checkOut:
      current.find((o) => o.checkInId === i.id) ??
      current.find((o) => o.checkInId === null && o.shiftId === i.shiftId) ??
      null,
  }));
}

/** One person's year, from their own rows. Rows for anybody else are ignored. */
export function serviceHours(
  personId: string,
  rows: { checkIns: ServiceCheckIn[]; checkOuts: ServiceCheckOut[]; shifts: ServiceShift[] },
): ServiceHours {
  const shifts = new Map(rows.shifts.map((s) => [s.id, s]));
  const lines: ServiceLine[] = [];
  for (const { checkIn: i, checkOut: out } of standingPairs(
    personId,
    rows.checkIns,
    rows.checkOuts,
  )) {
    const s = shifts.get(i.shiftId);
    if (!s) continue;
    // An attested check-in without a check-out is a chair's acknowledgment, not an hour.
    if (!out && i.byKind === 'attested') continue;
    const minutes = out
      ? (out.workedMinutes ?? Math.max(0, Math.round((out.at.getTime() - i.at.getTime()) / 60000)))
      : null;
    lines.push({
      shiftId: s.id,
      title: s.title,
      areaName: s.areaName,
      startsAt: s.startsAt,
      endsAt: s.endsAt,
      inAt: i.at,
      outAt: out?.at ?? null,
      minutes,
      state: out ? 'counted' : 'open',
      closedBy: !out
        ? null
        : out.byKind === 'import'
          ? 'import'
          : CHAIR_KINDS.has(out.byKind ?? '')
            ? 'chair'
            : 'booth',
      closedByPersonId: out?.byPersonId ?? null,
    });
  }
  lines.sort((a, b) => a.inAt.getTime() - b.inAt.getTime());
  return {
    personId,
    lines,
    countedMinutes: lines.reduce((n, l) => n + (l.minutes ?? 0), 0),
    openLines: lines.filter((l) => l.state === 'open').length,
  };
}

/** "10 h 10 m" — the drawing's short form. */
export function hoursShort(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${String(h)} h ${String(m)} m` : `${String(h)} h`;
}

/** "10 hours 10 minutes" — the letter's own words. */
export function hoursLong(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = `${String(h)} ${h === 1 ? 'hour' : 'hours'}`;
  return m ? `${hours} ${String(m)} ${m === 1 ? 'minute' : 'minutes'}` : hours;
}
